import { type MouseEvent, memo, type PointerEvent, useCallback, useMemo, useState } from 'react';

import { XIcon } from '@air/design-icons';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxInputTrigger,
  ComboboxList,
  ComboboxLoading,
} from '@air/primitive-combobox';
import { IconButton } from '@air/primitive-icon-button';
import { type SelectProps } from '@air/primitive-select';

import { SearchableBuiltInFontSelectOption } from '~/components/v2/components/shared/fonts/SearchableBuiltInFontSelectOption';
import { useFontsList } from '~/components/v2/hooks/queries/useFontsList';

export type SearchableBuiltInFontSelectProps = Pick<SelectProps, 'onValueChange' | 'value'> & {
  /** When provided and a family is selected, an X on the trigger clears the current font. */
  onClear?: () => void;
};

export const SearchableBuiltInFontSelect = memo(
  ({ onClear, onValueChange, value }: SearchableBuiltInFontSelectProps) => {
    const { data: fonts, isLoading } = useFontsList();
    const [open, setOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    const normalizedQuery = searchValue.trim().toLowerCase();

    const selectedLabel = useMemo(() => {
      if (!value) {
        return undefined;
      }

      return fonts?.data?.find((f) => f.family === value)?.family ?? value;
    }, [fonts?.data, value]);

    const filteredFonts = useMemo(() => {
      const list = fonts?.data ?? [];

      if (!normalizedQuery) {
        return list;
      }

      return list.filter((font) => font.family.toLowerCase().includes(normalizedQuery));
    }, [fonts?.data, normalizedQuery]);

    const handleOpenChange = useCallback((nextOpen: boolean) => {
      setOpen(nextOpen);

      if (!nextOpen) {
        setSearchValue('');
      }
    }, []);

    const handleSearchChange = useCallback((next: string) => {
      setSearchValue(next);
    }, []);

    const handleSelect = useCallback(
      (family: string) => {
        onValueChange?.(family);
        setSearchValue('');
        setOpen(false);
      },
      [onValueChange],
    );

    const handleClearPointerDown = useCallback((event: PointerEvent<HTMLButtonElement>) => {
      // Sibling overlay: Radix would otherwise treat this pointerdown as an outside click.
      event.stopPropagation();
    }, []);

    const handleClear = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onClear?.();
      },
      [onClear],
    );

    const showClearButton = !!onClear && !!value;

    return (
      <Combobox onOpenChange={handleOpenChange} open={open}>
        <div className="relative w-full">
          <ComboboxInputTrigger className={showClearButton ? 'pr-8' : undefined} disabled={isLoading} size="medium">
            {selectedLabel ? (
              <span className="truncate ads:text-primary text-gray-12">{selectedLabel}</span>
            ) : (
              <span className="ads:text-placeholder text-gray-10">Choose a font...</span>
            )}
          </ComboboxInputTrigger>
          {showClearButton ? (
            <IconButton
              adsIcon
              appearance="ghost"
              className="absolute top-1/2 right-1 z-1 -translate-y-1/2"
              color="gray"
              data-testid="SEARCHABLE_BUILT_IN_FONT_CLEAR"
              icon={XIcon}
              label="Clear font"
              onClick={handleClear}
              onPointerDown={handleClearPointerDown}
              size="extra-small"
            />
          ) : null}
        </div>
        <ComboboxContent shouldFilter={false} sideOffset={4}>
          <ComboboxInput onValueChange={handleSearchChange} placeholder="Search fonts..." value={searchValue} />

          {isLoading ? <ComboboxLoading>Loading...</ComboboxLoading> : <ComboboxEmpty>No fonts found.</ComboboxEmpty>}

          <ComboboxList>
            {filteredFonts.map((font) => (
              <SearchableBuiltInFontSelectOption
                fontFamily={font.family}
                isSelected={font.family === value}
                key={font.key}
                onSelect={handleSelect}
              />
            ))}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    );
  },
);

SearchableBuiltInFontSelect.displayName = 'SearchableBuiltInFontSelect';
